import type { DisabledNode, InstanceCustomisationView } from "@keydials/api-client";
import type { SegmentNoun } from "@keydials/dial-workspace";

/**
 * KEY-754: what the dial admin's Overview note says about the parts of a dial that are hidden.
 *
 * Tim's rule: a hidden part is hidden from EVERYONE, admins included — the point is a child picking up the family
 * phone and not seeing Christmas. So the note is for the people who can unhide (`canCustomise`) and nobody else:
 * for anyone else this returns null and nothing is drawn, because "1 section is hidden" is itself the thing a
 * child must not learn.
 *
 * Sections and action points only. Hidden items are usually tidying (an item a household doesn't use), and
 * listing them would bury the one line that matters.
 */
export interface HiddenPartsSummary {
  /** "2 sections and 1 action point are hidden" */
  headline: string;
  /** The hidden parts' names, sections first, in the order the server listed them. */
  names: string[];
}

const nameOf = (d: DisabledNode) => (d.label && d.label.trim()) || d.key;

export function hiddenPartsSummary(
  view: Pick<InstanceCustomisationView, "canCustomise" | "disabled"> | null | undefined,
  noun: Pick<SegmentNoun, "one" | "many">,
): HiddenPartsSummary | null {
  if (!view?.canCustomise) return null;
  const sections = view.disabled.filter((d) => d.nodeType === "Section");
  const segments = view.disabled.filter((d) => d.nodeType === "Segment");
  if (sections.length === 0 && segments.length === 0) return null;

  const parts: string[] = [];
  if (sections.length > 0) parts.push(`${sections.length} ${sections.length === 1 ? "section" : "sections"}`);
  if (segments.length > 0) parts.push(`${segments.length} ${segments.length === 1 ? noun.one : noun.many}`);
  const total = sections.length + segments.length;
  return {
    headline: `${parts.join(" and ")} ${total === 1 ? "is" : "are"} hidden`,
    names: [...sections, ...segments].map(nameOf),
  };
}

/** How long a reveal lasts when the admin asks from the note. The server clamps to 1–60. */
export const REVEAL_MINUTES = 15;

/**
 * The reveal banner's wording: "Showing hidden parts to you until 14:32". Null when there is no reveal running or
 * it has already passed (the client is about to re-read the dial). `now` and `locale` are parameters for tests.
 */
export function revealBannerText(
  expiresAtUtc: string | null | undefined,
  now: Date = new Date(),
  locale?: string,
): string | null {
  const ms = revealRemainingMs(expiresAtUtc, now);
  if (ms === null) return null;
  const until = new Date(expiresAtUtc as string).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `Showing hidden parts to you until ${until}`;
}

/** Milliseconds until the reveal stops, or null when there is none running (absent, unparseable or past). */
export function revealRemainingMs(expiresAtUtc: string | null | undefined, now: Date = new Date()): number | null {
  if (!expiresAtUtc) return null;
  const at = Date.parse(expiresAtUtc);
  if (Number.isNaN(at)) return null;
  const ms = at - now.getTime();
  return ms > 0 ? ms : null;
}

/** Where the note sends an admin to unhide for good. */
export const structureSettingsPath = (instanceId: string) => `/instances/${instanceId}/settings/structure`;
